import React, { useCallback, useState } from 'react';

import DeleteIcon from '@mui/icons-material/Delete';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  GridLegacy as Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { useUpdateBundleRules } from '~/hooks/apis/accommodation/useBundles';

import { Bundle, UpdateBundleRulesPayload } from '~/services/AccommodationService';

import BundleLpcRatePlanIdsPicker from '../components/BundleLpcRatePlanIdsPicker';
import BundlePromotionIdsPicker from '../components/BundlePromotionIdsPicker';
import {
  ALL_BEDBANK_SUPPLIERS,
  ALL_BOARD_CODES,
  ALL_CANCELLATION_POLICIES,
  BedbankSupplier,
  BoardCode,
  CancellationPolicy,
} from '../constants';

interface Props {
  bundle: Bundle;
}

interface OccupancyRow {
  adults: number | '';
  children: number | '';
  infants: number | '';
}

interface FormState {
  losMin: number | '';
  leadDaysMin: number | '';
  boardCodes: Array<BoardCode>;
  cancellationPolicies: Array<CancellationPolicy>;
  lpcRatePlanIds: Array<string>;
  allowAllLpcRatePlans: boolean;
  appliesToLpc: boolean;
  appliesToLpp: boolean;
  lppSupplierAllowlist: Array<BedbankSupplier>;
  lppPromotionIdAllowlist: Array<string>;
  includedGuests: Array<OccupancyRow>;
}

let nextRowKey = 0;
const allocateRowKey = () => `row-${++nextRowKey}`;

function buildInitialState(bundle: Bundle): FormState {
  const rule = bundle.rules?.[0];
  // Preserve an existing empty array — it means the admin intentionally cleared occupancy
  // gating. Only fall back to the default row when no rule has been saved yet.
  let capacities: Array<OccupancyRow>;
  if (rule) {
    capacities = (rule.includedGuests ?? []).map((c) => ({
      adults: c.adults ?? 0,
      children: c.children ?? 0,
      infants: c.infants ?? 0,
    }));
  } else {
    capacities = [{ adults: 2, children: 2, infants: 1 }];
  }
  return {
    losMin: rule?.losMin ?? 1,
    leadDaysMin: rule?.leadDaysMin ?? 1,
    boardCodes: (rule?.boardCodes ?? []) as Array<BoardCode>,
    cancellationPolicies: (rule?.cancellationPolicies ?? []) as Array<CancellationPolicy>,
    lpcRatePlanIds: rule?.lpcRatePlanIds ?? [],
    allowAllLpcRatePlans: rule?.allowAllLpcRatePlans ?? false,
    // Default false matches the spec for new rules. Existing rules were backfilled to true in the
    // svc-accommodation migration, so editing a legacy rule round-trips with both flags ticked.
    appliesToLpc: rule?.appliesToLpc ?? false,
    appliesToLpp: rule?.appliesToLpp ?? false,
    // Existing rules backfilled with all suppliers on the server; new rules default to empty so
    // admin must opt-in via the picker.
    lppSupplierAllowlist: (rule?.lppSupplierAllowlist ?? []) as Array<BedbankSupplier>,
    // Promotion id allowlist (HP-2565): empty array = no filter applied (bundle attaches to all
    // matching LPP rates). Admin opts in by adding promotion ids.
    lppPromotionIdAllowlist: rule?.lppPromotionIdAllowlist ?? [],
    includedGuests: capacities,
  };
}

function AccommodationBundleRuleTab({ bundle }: Props) {
  const [state, setState] = useState<FormState>(() => buildInitialState(bundle));
  const [rowKeys, setRowKeys] = useState<Array<string>>(() => state.includedGuests.map(allocateRowKey));
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const updateBundleRulesMutation = useUpdateBundleRules();

  const handleChange = useCallback(<K extends keyof FormState>(key: K, value: FormState[K]) => {
    setState((prev) => ({ ...prev, [key]: value }));
  }, []);

  const updateOccupancyRow = useCallback((index: number, key: keyof OccupancyRow, value: number | '') => {
    setState((prev) => ({
      ...prev,
      includedGuests: prev.includedGuests.map((row, i) => (i === index ? { ...row, [key]: value } : row)),
    }));
  }, []);

  const addOccupancyRow = useCallback(() => {
    setState((prev) => ({
      ...prev,
      includedGuests: [...prev.includedGuests, { adults: 0, children: 0, infants: 0 }],
    }));
    setRowKeys((prev) => [...prev, allocateRowKey()]);
  }, []);

  const removeOccupancyRow = useCallback((index: number) => {
    setState((prev) => ({
      ...prev,
      includedGuests: prev.includedGuests.filter((_, i) => i !== index),
    }));
    setRowKeys((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleSave = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault();
      setErrorMessage(null);

      const payload: UpdateBundleRulesPayload = {
        status: bundle.status,
        losMin: typeof state.losMin === 'number' ? state.losMin : 0,
        leadDaysMin: typeof state.leadDaysMin === 'number' ? state.leadDaysMin : 0,
        boardCodes: state.boardCodes,
        cancellationPolicies: state.cancellationPolicies,
        lpcRatePlanIds: state.lpcRatePlanIds,
        allowAllLpcRatePlans: state.allowAllLpcRatePlans,
        appliesToLpc: state.appliesToLpc,
        appliesToLpp: state.appliesToLpp,
        lppSupplierAllowlist: state.lppSupplierAllowlist,
        lppPromotionIdAllowlist: state.lppPromotionIdAllowlist,
        includedGuests: state.includedGuests.map((row) => ({
          adults: typeof row.adults === 'number' ? row.adults : 0,
          children: typeof row.children === 'number' ? row.children : 0,
          infants: typeof row.infants === 'number' ? row.infants : 0,
        })),
      };

      try {
        await updateBundleRulesMutation.mutateAsync({ bundleId: bundle.id, payload });
      } catch (err: unknown) {
        setErrorMessage(err instanceof Error ? err.message : 'Failed to save rule');
      }
    },
    [state, updateBundleRulesMutation, bundle.id, bundle.status],
  );

  const isSubmitting = updateBundleRulesMutation.isPending;
  const canSubmit = state.boardCodes.length > 0 && state.cancellationPolicies.length > 0 && !isSubmitting;

  return (
    <Box component="form" onSubmit={handleSave}>
      <Stack spacing={2}>
        <Typography variant="body2" color="text.secondary">
          The hotel rule controls which rates this bundle attaches to. A rate must satisfy length-of-stay, lead-days,
          board code, cancellation policy, and at least one of the occupancy maximums below. Tickets carry their own
          per-experience rule on top.
        </Typography>

        {errorMessage && <Alert severity="error">{errorMessage}</Alert>}

        <Grid container spacing={2}>
          <Grid item xs={6}>
            <TextField
              label="Min length of stay (nights)"
              type="number"
              value={state.losMin}
              onChange={(e) => handleChange('losMin', e.target.value === '' ? '' : Number(e.target.value))}
              inputProps={{ min: 1 }}
              fullWidth
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="Min lead days"
              type="number"
              value={state.leadDaysMin}
              onChange={(e) => handleChange('leadDaysMin', e.target.value === '' ? '' : Number(e.target.value))}
              inputProps={{ min: 0 }}
              fullWidth
              helperText="Minimum days between today and check-in."
            />
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>Board Codes</InputLabel>
              <Select
                multiple
                label="Board Codes"
                value={state.boardCodes}
                onChange={(e) => handleChange('boardCodes', e.target.value as Array<BoardCode>)}
                required
              >
                {ALL_BOARD_CODES.map((c) => (
                  <MenuItem key={c} value={c}>
                    {c}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>Cancellation Policies</InputLabel>
              <Select
                multiple
                label="Cancellation Policies"
                value={state.cancellationPolicies}
                onChange={(e) => handleChange('cancellationPolicies', e.target.value as Array<CancellationPolicy>)}
                required
              >
                {ALL_CANCELLATION_POLICIES.map((c) => (
                  <MenuItem key={c} value={c}>
                    {c}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="subtitle2" mb={0.5}>
              Product types
            </Typography>
            <Box display="flex" gap={2}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={state.appliesToLpc}
                    onChange={(e) => handleChange('appliesToLpc', e.target.checked)}
                  />
                }
                label="Apply to LPC"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={state.appliesToLpp}
                    onChange={(e) => handleChange('appliesToLpp', e.target.checked)}
                  />
                }
                label="Apply to LPP"
              />
            </Box>
            <Typography
              variant="caption"
              color={!state.appliesToLpc && !state.appliesToLpp ? 'warning.main' : 'text.secondary'}
              display="block"
            >
              {!state.appliesToLpc && !state.appliesToLpp
                ? 'Both unticked — this bundle will not attach to any rate until you tick LPC and/or LPP.'
                : 'Bundle only attaches to rate types that are ticked.'}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="subtitle2" mb={0.5}>
              LPP suppliers
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={state.lppSupplierAllowlist.length === ALL_BEDBANK_SUPPLIERS.length}
                  indeterminate={
                    state.lppSupplierAllowlist.length > 0 &&
                    state.lppSupplierAllowlist.length < ALL_BEDBANK_SUPPLIERS.length
                  }
                  onChange={(e) =>
                    handleChange('lppSupplierAllowlist', e.target.checked ? [...ALL_BEDBANK_SUPPLIERS] : [])
                  }
                />
              }
              label="All suppliers"
            />
            <Box display="flex" flexWrap="wrap" gap={1} mt={0.5}>
              {ALL_BEDBANK_SUPPLIERS.map((supplier) => (
                <FormControlLabel
                  key={supplier}
                  control={
                    <Checkbox
                      size="small"
                      checked={state.lppSupplierAllowlist.includes(supplier)}
                      onChange={(e) => {
                        const next = e.target.checked
                          ? [...state.lppSupplierAllowlist, supplier]
                          : state.lppSupplierAllowlist.filter((s) => s !== supplier);
                        handleChange('lppSupplierAllowlist', next);
                      }}
                    />
                  }
                  label={supplier}
                />
              ))}
            </Box>
            <Typography
              variant="caption"
              color={state.appliesToLpp && state.lppSupplierAllowlist.length === 0 ? 'warning.main' : 'text.secondary'}
              display="block"
            >
              {!state.appliesToLpp
                ? 'Enable "Apply to LPP" to restrict by supplier.'
                : state.lppSupplierAllowlist.length === 0
                  ? 'No suppliers ticked — this bundle will not attach to any LPP rate.'
                  : 'Bundle attaches to LPP rates whose winning supplier is ticked above.'}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={state.allowAllLpcRatePlans}
                  onChange={(e) => handleChange('allowAllLpcRatePlans', e.target.checked)}
                />
              }
              label="Allow all LPC rate plans (bypass allowlist)"
            />
            <Typography variant="caption" color="text.secondary" display="block">
              When ticked, the bundle attaches to every matching LPC rate regardless of the allowlist below.
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <BundleLpcRatePlanIdsPicker
              value={state.lpcRatePlanIds}
              onChange={(next) => handleChange('lpcRatePlanIds', next)}
              propertyIds={(bundle.offers ?? [])
                .filter((o) => o.entityType === 'HOTEL' && o.propertyId)
                .map((o) => o.propertyId as string)}
              disabled={state.allowAllLpcRatePlans}
            />
          </Grid>
          <Grid item xs={12}>
            <BundlePromotionIdsPicker
              value={state.lppPromotionIdAllowlist}
              onChange={(next) => handleChange('lppPromotionIdAllowlist', next)}
              propertyIds={(bundle.offers ?? [])
                .filter((o) => o.entityType === 'HOTEL' && o.propertyId)
                .map((o) => o.propertyId as string)}
              disabled={!state.appliesToLpp}
            />
          </Grid>
        </Grid>

        <Box>
          <Typography variant="subtitle2" mb={1}>
            Occupancy maximums
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={2}>
            A rate must fit within at least one of these maximums to be eligible. Add multiple rows to support different
            occupancy combinations (e.g. "2 adults" or "2 adults + 2 children + 1 infant"). Leave empty to defer
            occupancy gating entirely to each ticket's own rule.
          </Typography>

          <Stack spacing={2}>
            {state.includedGuests.map((row, index) => (
              <Box key={rowKeys[index]} border={1} borderColor="divider" borderRadius={1} p={2}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography variant="body2">Maximum #{index + 1}</Typography>
                  <IconButton
                    onClick={() => removeOccupancyRow(index)}
                    size="small"
                    aria-label="Remove occupancy maximum"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={4}>
                    <TextField
                      label="Max Adults"
                      type="number"
                      value={row.adults}
                      onChange={(e) =>
                        updateOccupancyRow(index, 'adults', e.target.value === '' ? '' : Number(e.target.value))
                      }
                      inputProps={{ min: 0 }}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField
                      label="Max Children"
                      type="number"
                      value={row.children}
                      onChange={(e) =>
                        updateOccupancyRow(index, 'children', e.target.value === '' ? '' : Number(e.target.value))
                      }
                      inputProps={{ min: 0 }}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField
                      label="Max Infants"
                      type="number"
                      value={row.infants}
                      onChange={(e) =>
                        updateOccupancyRow(index, 'infants', e.target.value === '' ? '' : Number(e.target.value))
                      }
                      inputProps={{ min: 0 }}
                      fullWidth
                    />
                  </Grid>
                </Grid>
              </Box>
            ))}
          </Stack>

          <Box mt={2}>
            <Button onClick={addOccupancyRow} variant="outlined">
              + Add maximum
            </Button>
          </Box>
        </Box>

        <Box display="flex" justifyContent="flex-end">
          <Button type="submit" variant="contained" disabled={!canSubmit}>
            {isSubmitting ? 'Saving…' : 'Save Rule'}
          </Button>
        </Box>
      </Stack>
    </Box>
  );
}

export default AccommodationBundleRuleTab;
